'use client';

import {
  QuizItem,
  QuizTypes,
  QuizRecommendedConcept,
} from '../../../types/quiz';
import Quiz from '../../../components/quiz/Quiz';
import { PageProps } from '../../../types/page';
import { CME } from '@osmosis/shared/types/learn';
import { CMEProvider, CMEType } from '../../../context/CMEContext';
import { QuizSettings } from '../../../lib/shared/quiz/quizSetting';
import AppPage from '../AppPageWrapper';
import { startTransition } from 'react';
import { revalidate } from '../../_actions/revalidate';
import { CacheTag } from '../../../lib/cache/tags';
import SkipLink from '../../../components/SkipLink';
import { LanguageProvider } from '../../../context/LanguageProvider';
import LearnAccessProvider from '../../../context/LearnAccessContext';
import { QueryClient, QueryClientProvider } from 'react-query';

const queryClient = new QueryClient({});

type Props = Omit<PageProps, 'children'> & {
  quizType: QuizTypes;
  items: Array<QuizItem>;
  learnNodeID?: number;
  relevantVideos?: QuizRecommendedConcept[];
  bundleID?: string;
  cme?: CME;
  showQuizStart?: boolean;
  isDailyPractice?: boolean;
  queueType?: string;
  learnName?: string;
  pathname?: string;
};

/**
 * Redner Quiz page.
 */
export default function QuizPage(props: Props): JSX.Element {
  const {
    items,
    bundleID,
    cme,
    showQuizStart = true,
    quizType,
    isDailyPractice = false,
    localSettings,
    queueType,
    learnNodeID,
    learnName,
    relevantVideos,
    pathname,
    ...rest
  } = props;
  // Calls server action to revalidate the user quiz data cache
  // tag which will invalidate any cached fetches that also use this tag (e.g. /learn pages)
  const revalidateData = () => {
    startTransition(() => {
      revalidate(CacheTag.USER_QUIZ_DATA_TAG);
    });
  };

  return (
    <QueryClientProvider client={queryClient}>
      <LanguageProvider
        user={props.user}
        bestLanguage={props.bestLanguage}
        serverIntlMessages={props.intlMessages}
        hasServerTranslationsFilter={props.hasServerTranslationsFilter}
      >
        <AppPage
          {...rest}
          renderOnClient
          scheme={localSettings?.quiz?.[QuizSettings.SCHEME]}
          key={`quiz-page-${pathname}`}
        >
          <CMEProvider
            key={`cme-provider-${pathname}`}
            type={CMEType.QUIZ}
            cookieName="cme:quiz:snackbar_already_seen"
            learnNodeId={learnNodeID}
            cme={cme}
            concept={learnName}
          >
            <SkipLink
              linkText="the question"
              linkUrl={['quiz-question']}
              modalId="modal-background"
            />
            <LearnAccessProvider>
              <Quiz
                items={items}
                bundleID={bundleID}
                quizType={quizType}
                showQuizStart={showQuizStart}
                isDailyPractice={isDailyPractice}
                queueType={queueType}
                quizSettings={localSettings?.quiz}
                revalidateData={revalidateData}
                relevantVideos={relevantVideos || []}
                learnName={learnName || ''}
              ></Quiz>
            </LearnAccessProvider>
          </CMEProvider>
        </AppPage>
      </LanguageProvider>
    </QueryClientProvider>
  );
}
